import { inject, injectable } from 'inversify'

import { InjectionTokens } from '@/controller/tokens'

import { Result } from '@/domain/protocols/Result'

import { ChecklistTemplate, IChecklistTemplateRepository } from './IChecklistTemplateRepository'
import { normalizeTemplates } from './normalizeTemplates'

import { DatasetCache } from '@/infra/datasetCache/DatasetCache'
import { readSourceStamp } from '@/infra/datasetCache/loadDatasetBytes'
import type { IHttpClient } from '@/infra/http/IHttpClient'
import { HTTP_TIMEOUTS } from '@/infra/http/timeouts'
import { toNonEmptyTrimmedString } from '@/utils/safe'

export const CHECKLIST_TEMPLATES_INVALID_PAYLOAD = 'CHECKLIST_TEMPLATES_INVALID_PAYLOAD'
export const CHECKLIST_TEMPLATES_REQUEST_FAILED = 'CHECKLIST_TEMPLATES_REQUEST_FAILED'

@injectable()
export class ChecklistTemplateRepository implements IChecklistTemplateRepository {
  // In-memory cache, online-first by project decision: nothing is persisted to disk. `undefined` =
  // never loaded; an array = loaded. The catalogue weighs ~1.68 MB, so it is downloaded on demand
  // (never at boot, and never registered in the lifeCycle) and then reused for the whole session.
  private templates: ChecklistTemplate[]
  // Dedupes concurrent callers (two screens of the builder opening at once) onto a single request.
  private loadPromise: Promise<Result<ChecklistTemplate[]>> | null = null

  constructor(
    @inject(InjectionTokens.HttpClient) private httpClient: IHttpClient,
    @inject(InjectionTokens.DatasetCache) private datasetCache: DatasetCache
  ) {}

  // The JSON catalogues live on the Spaces origin (`VITE_JETSTREAM_URL`), not on the CDN one, which
  // serves the `.frb`/`.fgb` tiles. `AircraftModelRepository` already downloads from here.
  protected getRemoteUrl(): string {
    return `${import.meta.env.VITE_JETSTREAM_URL}/global/checklist_templates.json`
  }

  async getChecklistTemplates(forceRefresh = false): Promise<Result<ChecklistTemplate[]>> {
    return this.ensureLoaded(forceRefresh)
  }

  async getChecklistTemplatesByAircraftModelId(aircraftModelId: string): Promise<Result<ChecklistTemplate[]>> {
    const modelId = toNonEmptyTrimmedString(aircraftModelId)
    // An aircraft with no model simply has no suggested template — that is an empty answer, not a
    // failure, and the caller renders the "no suggestion" banner either way.
    if (!modelId) return Result.ok([])

    const loadResult = await this.ensureLoaded()
    if (loadResult.isFailure) return Result.fail(loadResult.error)

    return Result.ok(loadResult.getValue().filter((template) => template.aircraftModelId === modelId))
  }

  // PRIVATE METHODS

  private async ensureLoaded(forceRefresh = false): Promise<Result<ChecklistTemplate[]>> {
    if (this.templates !== undefined && !forceRefresh) return Result.ok(this.templates)

    if (forceRefresh || !this.loadPromise) {
      this.loadPromise = this._getRemoteChecklistTemplates()
    }

    try {
      return await this.loadPromise
    } finally {
      // Cleared on success and on failure alike, so a failed download doesn't pin an error for the
      // whole session and the next caller gets a fresh attempt.
      this.loadPromise = null
    }
  }

  /**
   * Cache local com TTL, antes de ir a rede.
   *
   * O catalogo tem 1,68 MB sem compressao e a origem nao manda `cache-control`, entao sem isto cada
   * sessao que abre o construtor rebaixa tudo de novo. Mesma infra dos datasets aeronauticos
   * (`DatasetCache`), com TTL largo porque catalogo de template muda por publicacao, nao por hora.
   */
  private async _readFromCache(): Promise<ChecklistTemplate[] | null> {
    const cached = await this.datasetCache.read('checklistTemplates')

    if (!cached || cached.freshness === 'expired' || cached.kind !== 'text') return null

    try {
      const templates = normalizeTemplates(JSON.parse(cached.payload as string))

      return templates.length ? templates : null
    } catch {
      return null
    }
  }

  private async _getRemoteChecklistTemplates(): Promise<Result<ChecklistTemplate[]>> {
    const fromCache = await this._readFromCache()

    if (fromCache) {
      this.templates = fromCache

      return Result.ok(this.templates)
    }

    try {
      /*
       * Sem `withNetworkRetry` aqui: o `AxiosHttpClient` ja retenta flap de rede em GET (3 tentativas,
       * com jitter), e as duas camadas se MULTIPLICAVAM — 6 x 3 = **18 requisicoes** para um unico
       * carregamento, cada uma baixando ate 1,68 MB. O dono do retry e um so.
       *
       * A protecao que o retry existia para dar continua de pe: o `loadPromise` e limpo no `finally`
       * tanto em sucesso quanto em falha, entao um flap nao deixa o catalogo vazio pela sessao inteira
       * (era o bug dos modelos de aeronave) — a proxima chamada tenta de novo.
       *
       * Timeout de classe `dataset` (60 s), nao o default `interactive` (15 s): sao 1,68 MB SEM gzip,
       * e 15 s aborta isso em rede lenta. Foi exatamente esse o defeito da consulta de ADHP por ICAO.
       */
      const response = await this.httpClient.get<unknown>(this.getRemoteUrl(), {
        timeout: HTTP_TIMEOUTS.dataset
      })

      if (response.success === false || response.status < 200 || response.status >= 300) {
        return Result.fail(CHECKLIST_TEMPLATES_REQUEST_FAILED)
      }

      const templates = normalizeTemplates(response.response)
      // A catalogue that normalizes to nothing is a broken payload, not an empty catalogue: 51
      // templates are published. The cache is left unloaded so the next caller downloads again.
      if (!templates.length) return Result.fail(CHECKLIST_TEMPLATES_INVALID_PAYLOAD)

      // Replaced only once the payload is known to be usable, so a failure leaves the cache intact.
      this.templates = templates

      // Gravado DEPOIS da normalizacao, para nao persistir payload invalido. O `void` e deliberado: a
      // gravacao nao precisa gatear a resposta ao chamador.
      void this.datasetCache.write('checklistTemplates', JSON.stringify(response.response), {
        sourceStamp: readSourceStamp(response.headers),
        itemCount: templates.length
      })

      return Result.ok(this.templates)
    } catch {
      // Falha de transporte que sobreviveu as tentativas do `AxiosHttpClient`.
      return Result.fail(CHECKLIST_TEMPLATES_REQUEST_FAILED)
    }
  }
}
